import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

// The page-level sets survive variant section replacement without double counting.
const views = new Set();
const exposures = new Set();

/** Observes the main purchase area and forwards settled native events to the installed Clarity app. */
class KnPdpInsights extends Component {
  #controller;
  #observer;
  #retry;
  #pending = [];
  #attempts = 0;
  #root;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initialize();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    clearTimeout(this.#retry);
    this.#pending = [];
    super.disconnectedCallback();
  }

  #initialize() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    clearTimeout(this.#retry);
    this.#controller = new AbortController();
    this.#attempts = 0;
    this.#root = this.closest('[data-testid="product-information"]');
    if (!this.#root || !this.closest('#MainContent') || this.closest('dialog') || window.Shopify?.designMode) return;
    const { signal } = this.#controller;
    const id = this.dataset.productId;
    this.#enqueue('set', 'kn_product', this.dataset.productHandle || '');
    this.#enqueue('set', 'kn_pdp_layout', this.dataset.layout || 'standard');
    if (!views.has(id)) {
      views.add(id);
      this.#enqueue('event', 'kn_pdp_view');
    }
    const button = this.#root.querySelector('product-form-component [name="add"]');
    if (button instanceof HTMLButtonElement) {
      this.#observer = new IntersectionObserver((entries) => {
        if (entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= 0.5)) {
          if (!exposures.has(id)) {
            exposures.add(id);
            this.#enqueue('event', 'kn_buy_visible');
          }
          this.#observer?.disconnect();
        }
      }, { threshold: 0.5 });
      this.#observer.observe(button);
    }
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCart, { signal });
    document.addEventListener('click', this.#onCheckout, { signal, capture: true });
  }

  /** @param {import('@shopify/events').CartLinesUpdateEvent} event */
  #onCart = async (event) => {
    if (event.action !== 'add' || !(event.target instanceof Element) || !this.#root?.contains(event.target)) return;
    try {
      if (!event.promise) return;
      const result = await event.promise;
      if (!this.isConnected || this.#controller?.signal.aborted || result.detail?.didError || !result.cart) return;
      this.#enqueue('event', 'kn_add_success');
    } catch {
      // An unsuccessful request must never be reported as an addition.
    }
  };

  /** Records a checkout request, not a completed checkout or purchase. @param {MouseEvent} event */
  #onCheckout = (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('button[name="checkout"]');
    if (!(button instanceof HTMLButtonElement) || button.disabled || !button.form?.checkValidity()) return;
    this.#enqueue('event', 'kn_checkout_requested');
  };

  #enqueue(...args) {
    this.#pending.push(args);
    this.#flush();
  }

  #flush() {
    clearTimeout(this.#retry);
    if (typeof window.clarity === 'function') {
      for (const args of this.#pending.splice(0)) {
        try { window.clarity(...args); } catch { /* Optional insights must not interrupt purchasing. */ }
      }
      return;
    }
    // Do not install a tracker or change consent; wait briefly for the existing app.
    if (this.#attempts++ < 40) this.#retry = setTimeout(() => this.#flush(), 250);
    else this.#pending = [];
  }
}

if (!customElements.get('kn-pdp-insights')) customElements.define('kn-pdp-insights', KnPdpInsights);
